import { useState } from "react"
import { cloneDeep } from "lodash"
import { v4 as uuid } from "uuid"
import { useShallow } from "zustand/react/shallow"

// components
import { TextInput, Select } from "components-design-system"
import Arm from "../Arm/Arm"
import Sas from "../Sas/Sas"
import CreateArmModal from "../../../RJBaselineChars/subcomponents/CreateArmModal/CreateArmModal"
import DispersionRange from "containers/studies/Study/subcomponents/Authoring/RJS/widgets/components/cells/DispersionRange/DispersionRange.jsx"
import DispersionInputs from "containers/studies/Study/subcomponents/Authoring/RJS/widgets/components/cells/DispersionInputs/DispersionInputs.jsx"

// context
import useAuthoringDataStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringDataStore"
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"

// utils
import { moveLeftHelper, moveRightHelper } from "../../helpers/helpers"
import useResultsTableArmsStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableArmsStore"
import useBaselineActions from "../../../RJBaselineChars/hooks/useBaselineActions"

const Arms = ({ measure, measureIndex, outcomeData, setOutcomeData, schema, showUnitsAnalyzed }) => {
  const { onCreateSubjectAnalysisSetInBaseline } = useBaselineActions()

  const { outcomeArmOptions, baselineSas } = useResultsTableArmsStore()

  const { tableView } = useResultsTableViewStore()

  const { formData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      setFormData: state.setFormData,
    }))
  )

  const { readOnly } = useAuthoringViewStore(useShallow((state) => ({ readOnly: state.readOnly })))

  const [showAddArmDropdown, setShowAddArmDropdown] = useState(false)
  const [selectedArmType, setSelectedArmType] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const isCountable = measure.countable === "Countable"

  let armsSchema = schema.properties[tableView].properties.arms
  let armSchema = schema.properties[tableView].properties.arms.properties

  let armPropRows = Object.keys(armSchema).filter((key) => {
    if (showUnitsAnalyzed) return true
    else if (key === "num_units_analyzed") return false
    return true
  })

  const onAddArm = (selectedItem, createForAll) => {
    const filterDuplicateArms = (a, b) => {
      let ret = []
      b.forEach((bItem) => {
        let foundItem = a.find((aItem) => aItem.id === bItem.id)
        if (!foundItem) ret.push(bItem)
      })

      return ret
    }

    const stripId = (id) => {
      let segments = id.split(".")
      let uuid = segments[segments.length - 1]
      return uuid
    }

    const copyArms = (groups) => {
      let tempData = { ...outcomeData }

      const populateData = (registry, key, groups) => {
        let updatedGroups = [...groups]
        if (createForAll || tableView === registry) {
          let foundOMIndex = tempData[registry].outcome_measures.findIndex((item) => item.id === measure.id)
          if (foundOMIndex !== -1) {
            if (tempData[registry].outcome_measures[foundOMIndex][key]) {
              updatedGroups = []
              groups.forEach((group) => {
                let foundIndex = tempData[registry].outcome_measures[foundOMIndex][key].findIndex(
                  (item) => item.id === group.id
                )
                if (foundIndex === -1) updatedGroups.push(group)
                else {
                  let newGroup = {
                    ...group,
                    id: uuid(),
                  }
                  updatedGroups.push(newGroup)
                }
              })

              tempData[registry].outcome_measures[foundOMIndex][key] = [
                ...tempData[registry].outcome_measures[foundOMIndex][key],
                ...updatedGroups,
              ]
            } else {
              tempData[registry].outcome_measures[foundOMIndex][key] = [...updatedGroups]
            }

            tempData[registry].outcome_measures[foundOMIndex].measure_rows.forEach((row) => {
              row.categories.forEach((cat) => {
                let values = cat.values ? [...cat.values] : []
                updatedGroups.map((group) => {
                  let foundIndex = values.findIndex((value) => value.group_id === group.id)

                  if (foundIndex === -1) {
                    values.push({
                      group_id: group.id,
                      registry_arm_id: group.registry_arm_id,
                      value: null,
                    })
                  }
                })

                cat.values = values
              })
            })
          }
        }
      }

      Object.keys(outcomeData).forEach((registry) => {
        const cloneGroups = cloneDeep(groups)
        if (createForAll || tableView === registry) {
          if (registry === "eudract" && selectedItem?.id !== "copy-pf") {
            let [sets, arms] = createSubjectAnalysisSet(cloneGroups, tempData)

            populateData(registry, "arms", arms)
            populateData(registry, "subject_analysis_sets", sets)
            onCreateSubjectAnalysisSetInBaseline(sets)
          } else if (registry !== "eudract") {
            populateData(registry, "arms", cloneGroups)
          }
        }
      })

      setOutcomeData(tempData)
    }

    const createSubjectAnalysisSet = (groups, tempData) => {
      let sets = []
      let arms = []
      for (let group of groups) {
        let found = false
        if (formData?.results?.participant_flow?.participant_flow_table?.eudract_periods) {
          for (let period of formData.results.participant_flow.participant_flow_table.eudract_periods) {
            for (let arm of period.arms) {
              if (group.registry_arm_id === arm.registry_arm_id) {
                found = true
                arms.push(group)
                break
              }
            }
            if (found) break
          }
        }
        if (found === false) {
          sets.push(group)
        }
      }
      return [sets, arms]
    }

    const formatArmIds = (arms) => {
      return arms.map((arm) => {
        let oldArmIds = arm?.id?.split(".")
        let newId = arm?.id
        if (oldArmIds?.[1] && oldArmIds?.[0]?.includes("Participant")) {
          newId = "OutcomeMeasure." + measure.id + "-OutcomeRptGroup." + oldArmIds[1]
        } else if (oldArmIds?.[0] && !oldArmIds?.[1]) {
          newId = "OutcomeMeasure." + measure.id + "-OutcomeRptGroup." + oldArmIds[0]
        }
        return {
          ...arm,
          id: newId,
        }
      })
    }

    const isPeriodArms = () => {
      if (formData?.results?.participant_flow?.participant_flow_table?.eudract_periods) {
        let foundIndex = formData.results.participant_flow.participant_flow_table.eudract_periods.findIndex(
          (period) => period.id === selectedItem.id
        )
        if (foundIndex !== -1) {
          let period = formData.results.participant_flow.participant_flow_table.eudract_periods[foundIndex]
          return formData.results.participant_flow.participant_flow_table.eudract_periods[foundIndex].arms.map(
            (arm) => {
              return {
                ...arm,
                registry_arm_id: `${period.id}:${arm.id}`,
              }
            }
          )
        }
      }
      return null
    }

    switch (selectedItem.id) {
      case "copy-pf":
        if (formData?.results?.participant_flow?.participant_flow_table?.global_arms) {
          let pfArms = cloneDeep(formData.results?.participant_flow.participant_flow_table.global_arms)
          let registryArms = [...measure.arms]
          let filteredArms = [...filterDuplicateArms(registryArms, pfArms)]

          copyArms(formatArmIds(filteredArms))
        }
        break
      case "create-new":
        let id = uuid()
        let group = {
          id,
          registry_arm_id: `OutcomeMeasure.${measure.id}-OutcomeRptGroup.${id}`,
          title: "",
          description: "",
          is_subject_analysis_set: true,
          type: "",
          baseline_analysis_population: undefined,
          subjects_analyzed: undefined,
        }
        copyArms(formatArmIds([group]))
        break
      default:
        let arms = isPeriodArms()

        if (arms) {
          let pfArms = []
          if (selectedItem) {
            let foundPeriodIndex =
              formData.results?.participant_flow?.participant_flow_table?.eudract_periods?.findIndex(
                (period) => period.id === selectedItem.id
              )
            if (foundPeriodIndex !== -1) {
              pfArms = cloneDeep(
                formData.results?.participant_flow?.participant_flow_table?.eudract_periods[foundPeriodIndex].arms
              )
            }
          }
          let registryArms = [...measure.arms]
          let filteredArms = [...filterDuplicateArms(registryArms, pfArms)]
          copyArms(formatArmIds(filteredArms), true)
        } else {
          let group = baselineSas.find((item) => item.id.includes(selectedItem.id))

          if (group) {
            let formattedGroups = formatArmIds(cloneDeep([group]))
            let sets = measure.subject_analysis_sets || []
            let arms = measure.arms || []

            let groups = [...sets, ...arms]
            let filteredArms = filterDuplicateArms(groups, formattedGroups).map((group) => {
              const id = `OutcomeMeasure.${measure.id}-OutcomeRptGroup.${stripId(group?.id)}`

              return {
                ...group,
                id,
              }
            })

            copyArms(filteredArms, false)
          }
        }
        break
    }
    onHideModal()
  }

  const handleLeft = (group) => {
    let tempData = { ...outcomeData }
    Object.keys(tempData).forEach((registry) => {
      let foundOMIndex = tempData[registry].outcome_measures.findIndex((item) => item.id === measure.id)
      if (foundOMIndex !== -1) {
        let om = tempData[registry].outcome_measures[foundOMIndex]
        let foundGroupIndex = om.arms.findIndex((item) => item.id === group.id)
        if (foundGroupIndex !== -1 && foundGroupIndex !== 0) {
          moveLeftHelper(om, "arms", foundGroupIndex)
          om.measure_rows.forEach((row) => {
            row.categories.forEach((category) => {
              let tempValues = [...category.values]
              let foundIndex = tempValues.findIndex((item) => item.group_id === group.id)

              if (foundIndex !== -1 && foundIndex !== 0) {
                let saved = tempValues[foundIndex - 1]
                tempValues[foundIndex - 1] = tempValues[foundIndex]
                tempValues[foundIndex] = saved
                category.values = [...tempValues]
              }
            })
          })
        } else if (registry === "eudract" && foundGroupIndex === -1) {
          foundGroupIndex = om?.subject_analysis_sets?.findIndex((item) => item.id === group.id)
          if (foundGroupIndex !== -1) {
            moveLeftHelper(om, "subject_analysis_sets", foundGroupIndex)
            om.measure_rows.forEach((row) => {
              row.categories.forEach((category) => {
                let tempValues = [...category.values]
                let foundIndex = tempValues.findIndex((item) => item.group_id === group.id)

                if (foundIndex !== -1 && foundIndex !== 0) {
                  let saved = tempValues[foundIndex - 1]
                  tempValues[foundIndex - 1] = tempValues[foundIndex]
                  tempValues[foundIndex] = saved
                  category.values = [...tempValues]
                }
              })
            })
          }
        }
      }
    })
    setOutcomeData(tempData)
  }

  const handleRight = (group) => {
    let tempData = { ...outcomeData }
    Object.keys(tempData).forEach((registry) => {
      let foundOMIndex = tempData[registry].outcome_measures.findIndex((item) => item.id === measure.id)
      if (foundOMIndex !== -1) {
        let om = tempData[registry].outcome_measures[foundOMIndex]
        let foundGroupIndex = om.arms.findIndex((item) => item.id === group.id)
        if (foundGroupIndex !== -1 && foundGroupIndex !== om.arms.length - 1) {
          moveRightHelper(om, "arms", foundGroupIndex)
          om.measure_rows.forEach((row) => {
            row.categories.forEach((category) => {
              let tempValues = [...category.values]
              let foundIndex = tempValues.findIndex((item) => item.group_id === group.id)

              if (foundIndex !== -1 && foundIndex !== category.values.length - 1) {
                let saved = tempValues[foundIndex + 1]
                tempValues[foundIndex + 1] = tempValues[foundIndex]
                tempValues[foundIndex] = saved
                category.values = [...tempValues]
              }
            })
          })
        } else if (registry === "eudract" && foundGroupIndex === -1) {
          foundGroupIndex = om?.subject_analysis_sets?.findIndex((item) => item.id === group.id)
          if (foundGroupIndex !== -1) {
            moveRightHelper(om, "subject_analysis_sets", foundGroupIndex)
            om.measure_rows.forEach((row) => {
              row.categories.forEach((category) => {
                let tempValues = [...category.values]
                let foundIndex = tempValues.findIndex((item) => item.group_id === group.id)

                if (foundIndex !== -1 && foundIndex !== category.values.length - 1) {
                  let saved = tempValues[foundIndex + 1]
                  tempValues[foundIndex + 1] = tempValues[foundIndex]
                  tempValues[foundIndex] = saved
                  category.values = [...tempValues]
                }
              })
            })
          }
        }
      }
    })
    setOutcomeData(tempData)
  }

  const handleDelete = (group, applyAll) => {
    let tempData = { ...outcomeData }
    Object.keys(tempData).forEach((registry) => {
      if (applyAll === true || registry === tableView) {
        let foundOMIndex = tempData[registry].outcome_measures.findIndex((item) => item.id === measure.id)
        if (foundOMIndex !== -1) {
          let om = tempData[registry].outcome_measures[foundOMIndex]
          let foundIndex = om.arms.findIndex((item) => item.id === group.id)
          if (foundIndex !== -1) om.arms.splice(foundIndex, 1)

          if (registry === "eudract" && om.subject_analysis_sets) {
            foundIndex = om?.subject_analysis_sets?.findIndex((item) => item.id === group.id)
            if (foundIndex !== -1) om.subject_analysis_sets.splice(foundIndex, 1)
          }

          om.measure_rows.forEach((row) => {
            row?.categories?.forEach((category) => {
              let tempValues = []
              if (category.values) {
                tempValues = [...category.values]
              }
              let foundIndex = category?.values?.findIndex((item) => item.group_id === group.id)

              if (foundIndex !== -1) {
                tempValues.splice(foundIndex, 1)
                category.values = [...tempValues]
              }
            })
          })

          if (om?.outcome_measure_analysis?.length > 0) {
            om.outcome_measure_analysis.forEach((analysis) => {
              if (analysis?.outcome_reporting_groups?.length > 0) {
                let foundIndexOfArm = analysis.outcome_reporting_groups.findIndex((arm) => arm.id === group.id)
                if (foundIndexOfArm !== -1) {
                  analysis.outcome_reporting_groups.splice(foundIndexOfArm, 1)
                }
              }
            })
          }
        }
      }
    })

    setOutcomeData(tempData)
  }

  const onShowModal = (selectedItem) => {
    setSelectedArmType(selectedItem)
    setShowModal(true)
  }

  const onHideModal = () => {
    setSelectedArmType(null)
    setShowModal(false)
  }

  const renderSubjectAnalysisSet = () => {
    return measure?.subject_analysis_sets
      ?.filter((set) => set)
      .map((set, i) => {
        return (
          <Sas
            key={`${measure.id}-${set.id}-${i}`}
            set={set}
            sets={measure.subject_analysis_sets}
            measure={measure}
            handleLeft={handleLeft}
            handleRight={handleRight}
            colIndex={i}
            rowIndex={0}
            handleDelete={handleDelete}
            setOutcomeData={setOutcomeData}
            outcomeData={outcomeData}
            measureIndex={measureIndex}
          />
        )
      })
  }

  const getRowSpan = () => {
    if (tableView === "eudract") {
      return 2
    } else {
      return showUnitsAnalyzed ? 4 : 3
    }
  }

  const getColSpan = () => {
    if (measure?.arms?.length === 0 && measure?.subject_analysis_sets?.length > 0) {
      return 2
    }
    return 1
  }

  const onChangeConditionalFields = (e, arm, fieldKey) => {
    let registry = "eudract"
    let tempData = { ...outcomeData }

    let foundOMIndex = tempData[registry].outcome_measures.findIndex((item) => item.id === measure.id)
    if (foundOMIndex !== -1) {
      let armIndex = tempData[registry].outcome_measures[foundOMIndex].arms.findIndex((item) => item.id === arm.id)
      let sasIndex = tempData[registry].outcome_measures[foundOMIndex]?.subject_analysis_sets?.findIndex(
        (item) => item.id === arm.id
      )
      let foundGroupIndex = armIndex !== -1 ? armIndex : sasIndex
      let groupKey = armIndex !== -1 ? "arms" : "subject_analysis_sets"
      if (foundGroupIndex !== -1) {
        tempData[registry].outcome_measures[foundOMIndex][groupKey][foundGroupIndex][fieldKey] = e
      }
    }

    setOutcomeData(tempData)
  }

  const renderConditionalFields = (groupType) => {
    let condSchemas = Object.keys(armsSchema.conds).filter((key) => {
      let fieldSchema = armsSchema.conds[key]
      let canShow = true
      if (fieldSchema.paramTypeCond) {
        if (fieldSchema.paramTypeCond.findIndex((param) => param === measure.parameter_type) === -1) canShow = false
      }

      if (fieldSchema.dispTypeCond) {
        if (fieldSchema.dispTypeCond.findIndex((param) => param === measure.dispersion_type) === -1) canShow = false
      }
      return canShow
    })

    const renderFields = (arm) => {
      if (condSchemas.length === 3) {
        return (
          <DispersionRange
            parameterType={measure.parameter_type}
            dispersionType={measure.dispersion_type}
            dispersionValue={arm?.dispersion_value}
            dispersionValueUpperBound={arm?.dispersion_value_upper_bound}
            cell={arm}
            cellArmId={arm?.id}
            onChange={(e, regArmId, fieldKey) => onChangeConditionalFields(e, arm, fieldKey)}
          />
        )
      } else if (condSchemas.length === 2) {
        return (
          <DispersionInputs
            parameterType={measure.parameter_type}
            dispersionType={measure.dispersion_type}
            dispersionValue={arm?.dispersion_value}
            cell={arm}
            cellArmId={arm?.id}
            onChange={(e, regArmId, fieldKey) => onChangeConditionalFields(e, arm, fieldKey)}
          />
        )
      } else if (condSchemas.length === 1 || isCountable) {
        let label = measure.parameter_type
        if (measure.countable === "Countable") label = "Count"
        return (
          <TextInput
            label={label}
            readOnly={readOnly}
            value={arm?.value}
            onChange={(e) => onChangeConditionalFields(e.target.value, arm, "value")}
          />
        )
      }
    }

    if (condSchemas.length === 0 && !isCountable) return

    return (
      <tr
        key={`${tableView}-arm-row-conditional-row ${
          groupType === "subject_analysis_sets" ? "subject-analysis-set" : ""
        }`}
      >
        <td
          className={`header-column ${groupType === "subject_analysis_sets" ? "subject-analysis-set" : ""}`}
          colSpan={2}
        ></td>
        {measure?.[groupType]?.map((group, cI) => {
          return (
            <td
              className={`${groupType === "subject_analysis_sets" ? "subject-analysis-set" : ""}`}
              key={`${group.id}-om-arm`}
            >
              {renderFields(group)}
            </td>
          )
        })}
      </tr>
    )
  }

  const rows = measure.measure_rows || []

  return (
    <>
      {armPropRows.map((key, rI) => {
        return (
          <tr key={`${tableView}-arm-row-${key}-${rI}`}>
            <td
              className="header-column"
              colSpan={2}
            >
              {armSchema[key].title}
            </td>
            {measure?.arms?.map((arm, cI) => {
              return (
                <Arm
                  key={`${arm?.id}-${cI}`}
                  arm={arm}
                  colIndex={cI}
                  rowIndex={rI}
                  arms={measure.arms}
                  fieldKey={key}
                  value={arm?.[key] || ""}
                  schema={armSchema}
                  armsSchema={armsSchema}
                  measure={measure}
                  measureIndex={measureIndex}
                  handleLeft={() => handleLeft(arm)}
                  handleRight={() => handleRight(arm)}
                  handleDelete={handleDelete}
                  outcomeData={outcomeData}
                  setOutcomeData={setOutcomeData}
                />
              )
            })}
            {rI === 0 && readOnly !== true && (
              <td
                className="text-center add-arm-action-col"
                rowSpan={getRowSpan()}
                colSpan={getColSpan()}
              >
                {showAddArmDropdown ? (
                  <Select
                    type="dropdown"
                    placeholder="Select arm"
                    options={outcomeArmOptions.filter((item) => {
                      if (tableView === "eudract" && item.id === "copy-pf") return false
                      return true
                    })}
                    readOnly={readOnly}
                    onChange={(e) => {
                      let foundItem = outcomeArmOptions.find((item) => item.id === e)
                      onShowModal(foundItem)
                    }}
                    value={null}
                  />
                ) : (
                  <i
                    onClick={() => setShowAddArmDropdown(true)}
                    className="fal fa-plus-circle add-arm-icon"
                  />
                )}
              </td>
            )}
          </tr>
        )
      })}
      {armsSchema.conds && rows.length === 0 && renderConditionalFields("arms")}
      {measure?.subject_analysis_sets?.length > 0 && (
        <tr>
          <td
            className="header-column subject-analysis-set"
            colSpan={2}
          >
            Subject Analysis Sets
          </td>
          {renderSubjectAnalysisSet()}
        </tr>
      )}
      {armsSchema.conds &&
        measure?.subject_analysis_sets?.length > 0 &&
        rows.length === 0 &&
        renderConditionalFields("subject_analysis_sets")}
      <CreateArmModal
        onAddArm={onAddArm}
        showModal={showModal}
        setShowModal={onHideModal}
        selectedArmType={selectedArmType}
        source="om"
      />
    </>
  )
}

export default Arms
